import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  LockOutlined as LockOutlinedIcon,
} from "@mui/icons-material";
import { indigo } from "@mui/material/colors";
import useAdminContext from "../../store/contexts/AdminContext";

const AdminChangePassword = () => {
  const {
    authState: { loading, error, message },
    changePasswordAdminApiCall,
    clearErrors,
  } = useAdminContext();

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    clearErrors();
  }, [clearErrors]);

  useEffect(() => {
    if (message) {
      setSuccess(true);
      // Reset form after successful password change
      setFormData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    }
  }, [message]);

  const handleTogglePasswordVisibility = (field) => {
    switch (field) {
      case "current":
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case "new":
        setShowNewPassword(!showNewPassword);
        break;
      case "confirm":
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear field error when user types
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }

    // Clear success message when user starts typing again
    if (success) {
      setSuccess(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.current_password) {
      errors.current_password = "Current password is required";
    }

    if (!formData.new_password) {
      errors.new_password = "New password is required";
    } else if (formData.new_password.length < 8) {
      errors.new_password = "Password must be at least 8 characters";
    }

    if (!formData.confirm_password) {
      errors.confirm_password = "Please confirm your new password";
    } else if (formData.new_password !== formData.confirm_password) {
      errors.confirm_password = "Passwords do not match";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      await changePasswordAdminApiCall(formData);
    }
  };

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={8} lg={6}>
        <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <LockOutlinedIcon sx={{ color: indigo[700], mr: 1 }} />
            <Typography variant="h5" component="h1">
              Change Password
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="current_password"
              label="Current Password"
              type={showCurrentPassword ? "text" : "password"}
              id="current_password"
              value={formData.current_password}
              onChange={handleChange}
              error={!!formErrors.current_password}
              helperText={formErrors.current_password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle current password visibility"
                      onClick={() => handleTogglePasswordVisibility("current")}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="new_password"
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              id="new_password"
              value={formData.new_password}
              onChange={handleChange}
              error={!!formErrors.new_password}
              helperText={formErrors.new_password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => handleTogglePasswordVisibility("new")}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirm_password"
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              id="confirm_password"
              value={formData.confirm_password}
              onChange={handleChange}
              error={!!formErrors.confirm_password}
              helperText={formErrors.confirm_password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => handleTogglePasswordVisibility("confirm")}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="button"
                variant="outlined"
                sx={{ mr: 2 }}
                onClick={() => {
                  setFormData({
                    current_password: "",
                    new_password: "",
                    confirm_password: "",
                  });
                  setFormErrors({});
                }}
                disabled={loading}
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  backgroundColor: indigo[700],
                  "&:hover": {
                    backgroundColor: indigo[800],
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Change Password"
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default AdminChangePassword;
